import { LeftOutlined, SearchOutlined } from "@ant-design/icons"
import { Button, Card, Flex, Select, Skeleton, Tooltip, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"

import { SearchBar } from "../../../../../../../components/Input/SearchBar.tsx"
import { RoutesPaths } from "../../../../../../../constants/routes.ts"
import { type AttachmentFilters } from "../../../../../../Album/redux/album.types.ts"
import { type AttachmentFilterOptions } from "../../../../../../Album/types/attachment.types.ts"
import { useFilterDrawerCount } from "./ProjectAllFilesFilterDrawer.tsx"

const { Text } = Typography

interface ProjectAllFilesFiltersProps {
  filters: AttachmentFilters
  projectId: string
  showRelatedFilter: boolean
  onFilterChange: (data: Partial<AttachmentFilters>) => void
  getFilterOptions: () => Promise<AttachmentFilterOptions | null>
}

export function ProjectAllFilesFilters({
  filters,
  projectId,
  showRelatedFilter,
  onFilterChange,
  getFilterOptions
}: ProjectAllFilesFiltersProps) {
  const [fetching, setFetching] = useState(false)
  const [options, setOptions] = useState<AttachmentFilterOptions | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const advancedFiltersCount = useFilterDrawerCount(filters)

  const handleGetFilterOptions = useCallback(async () => {
    try {
      setFetching(true)
      const res = await getFilterOptions()
      setOptions(res)
    } catch (e) {
      console.log("Error attachment filter options:", e)
    } finally {
      setFetching(false)
    }
  }, [getFilterOptions])

  const onSelectFilterChange =
    (key: keyof AttachmentFilters) => (value: unknown) => {
      const updates: Partial<AttachmentFilters> = { [key]: value || null }

      // Clear related ID filters when model_type changes
      if (key === "model_type") {
        updates.milestone = null
        updates.invoice = null
        updates.checklist_item = null
        updates.financial_item = null
      }

      onFilterChange(updates)
    }

  const onSearchChange = (value: string | null) => {
    onFilterChange({ q: value || null })
  }

  useEffect(() => {
    void handleGetFilterOptions()
  }, [handleGetFilterOptions])

  if (fetching) {
    return <Skeleton.Node className="w-full h-12" active />
  }

  return (
    <>
      <Card classNames={{ body: "p-2 space-y-2" }}>
        <Flex justify="space-between" align="center" wrap="wrap" gap="small">
          <Flex align="center" gap="middle" wrap="wrap">
            <Link to={`${RoutesPaths.project.view(projectId).files}?nav=albums`}>
              <Tooltip title="Back to albums">
                <Button icon={<LeftOutlined />} />
              </Tooltip>
            </Link>

            <Flex align="center" gap={8}>
              <Text type="secondary">File Type:</Text>
              <Select
                allowClear
                value={filters?.file_type}
                style={{ width: 120 }}
                placeholder="All types"
                onChange={onSelectFilterChange("file_type")}
                options={options?.file_types}
              />
            </Flex>

            {showRelatedFilter && (
              <Flex align="center" gap={8}>
                <Text type="secondary">Related To:</Text>
                <Select
                  allowClear
                  value={filters?.model_type}
                  style={{ width: 150 }}
                  placeholder="All"
                  onChange={onSelectFilterChange("model_type")}
                  options={options?.model_types}
                />
              </Flex>
            )}

            <Flex align="center" gap={8}>
              <Text type="secondary">Date:</Text>
              <Select
                allowClear
                value={filters?.date_range}
                style={{ width: 130 }}
                placeholder="Any time"
                onChange={onSelectFilterChange("date_range")}
                options={options?.date_ranges}
              />
            </Flex>

            {/*<FilterDrawerTrigger*/}
            {/*  onClick={() => setDrawerOpen(true)}*/}
            {/*  activeCount={advancedFiltersCount}*/}
            {/*/>*/}
          </Flex>

          <SearchBar
            placeholder="Search files..."
            prefix={<SearchOutlined className="text-gray-400" />}
            className="w-48 md:w-64 flex-none"
            defaultValue={filters?.q || ""}
            onSearch={onSearchChange}
          />
        </Flex>
      </Card>

      {/*<ProjectAllFilesFilterDrawer*/}
      {/*  open={drawerOpen}*/}
      {/*  onClose={() => setDrawerOpen(false)}*/}
      {/*  filters={filters}*/}
      {/*  options={options}*/}
      {/*  onFilterChange={onFilterChange}*/}
      {/*/>*/}
    </>
  )
}
